import { useEffect, type ReactNode } from "react";
import { IconButton } from "./Button";

interface DialogProps {
  readonly children: ReactNode;
  readonly title: string;
  readonly description?: string;
  readonly eyebrow?: string;
  readonly size?: "medium" | "large";
  readonly busy?: boolean;
  readonly onClose: () => void;
}

/**
 * Accessible modal shell shared by application forms and secret disclosure.
 * Escape and backdrop close are disabled while a mutation is in progress to
 * prevent users from losing the result of a partially completed operation.
 */
export function Dialog({
  children,
  title,
  description,
  eyebrow,
  size = "medium",
  busy = false,
  onClose,
}: DialogProps) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [busy, onClose]);

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        aria-describedby={description === undefined ? undefined : "dialog-description"}
        aria-labelledby="dialog-title"
        aria-modal="true"
        className={`dialog dialog-${size}`}
        role="dialog"
      >
        <header className="dialog-header">
          <div>
            {eyebrow === undefined ? null : <p className="eyebrow">{eyebrow}</p>}
            <h2 id="dialog-title">{title}</h2>
            {description === undefined ? null : <p id="dialog-description">{description}</p>}
          </div>
          <IconButton disabled={busy} icon="x" label="关闭" onClick={onClose} />
        </header>
        {children}
      </section>
    </div>
  );
}
